/**
 * useDataset  —  centralised state management for the active workbook.
 *
 * Holds:  datasetId, filename, sheetNames, sheetsInfo, activeSheet,
 *         workbookOverview, loading, error
 * Exposes:  loadFromUpload, loadFromGoogleSheet, switchSheet, reset
 *
 * The hook auto-selects the first *usable* sheet after import.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import * as api from '../services/api';
import { toast } from 'sonner';

const SESSION_KEY = 'sheetanalyst_session';

export default function useDataset() {
  const [datasetId, setDatasetId]               = useState(null);
  const [filename, setFilename]                 = useState('');
  const [sheetNames, setSheetNames]             = useState([]);
  const [sheetsInfo, setSheetsInfo]              = useState({});
  const [activeSheet, setActiveSheet]           = useState('');
  const [workbookOverview, setWorkbookOverview] = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState('');
  
  // Analysis state
  const [deepAnalysisBySheet, setDeepAnalysisBySheet] = useState({});
  const [analysisLoading, setAnalysisLoading]         = useState(false);
  const [analysisError, setAnalysisError]             = useState('');

  /* ---- pick the first usable sheet (fallback: first sheet) ---- */
  const _firstUsable = (names, info) => {
    const usable = names.find(
      (n) => info[n]?.sheet_status?.usable !== false,
    );
    return usable || names[0] || '';
  };

  /* ---- internal: hydrate state from any successful response ---- */
  const _hydrate = useCallback((data) => {
    setDatasetId(data.dataset_id);
    setFilename(data.filename);
    setSheetNames(data.sheet_names);
    setSheetsInfo(data.sheets_info);
    setWorkbookOverview(data.workbook_overview || null);
    const first = _firstUsable(data.sheet_names, data.sheets_info);
    setActiveSheet(first);
    setError('');

    // Persist session info for refresh recovery
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        datasetId: data.dataset_id,
        filename: data.filename,
        activeSheet: first,
        sheetNames: data.sheet_names,
      }));
    } catch (e) { /* quota exceeded, ignore */ }

    const usable = data.workbook_overview?.usable_sheets ?? data.sheet_names.length;
    const total  = data.workbook_overview?.total_sheets  ?? data.sheet_names.length;
    toast.success(
      `Loaded ${data.filename} — ${usable}/${total} sheet(s) usable`,
    );
  }, []);

  /* ---- actions ------------------------------------------------- */

  const loadFromUpload = useCallback(async (file) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.uploadFile(file);
      _hydrate(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [_hydrate]);

  const loadFromGoogleSheet = useCallback(async (url) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.importGoogleSheet(url);
      _hydrate(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [_hydrate]);

  const switchSheet = useCallback((name) => {
    setActiveSheet(name);
  }, []);

  const reset = useCallback(() => {
    setDatasetId(null);
    setFilename('');
    setSheetNames([]);
    setSheetsInfo({});
    setActiveSheet('');
    setWorkbookOverview(null);
    setError('');
    setDeepAnalysisBySheet({});
    setAnalysisError('');
    localStorage.removeItem(SESSION_KEY);
  }, []);
  
  const loadFromHistory = useCallback(async (historyDatasetId) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getDataset(historyDatasetId);
      _hydrate(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [_hydrate]);

  const analyzeCurrentSheet = useCallback(async () => {
    if (!datasetId || !activeSheet) {
      toast.error('No sheet selected to analyze.');
      return;
    }
    
    setAnalysisLoading(true);
    setAnalysisError('');
    
    try {
      const result = await api.analyzeSheet(datasetId, activeSheet);
      setDeepAnalysisBySheet((prev) => ({
        ...prev,
        [activeSheet]: result
      }));
      toast.success(`Analysis complete for ${activeSheet}`);
    } catch (err) {
      setAnalysisError(err.message);
      toast.error(`Analysis failed: ${err.message}`);
    } finally {
      setAnalysisLoading(false);
    }
  }, [datasetId, activeSheet]);

  /* ---- derived ------------------------------------------------- */

  const currentSheetInfo = useMemo(
    () => sheetsInfo[activeSheet] || null,
    [sheetsInfo, activeSheet],
  );
  
  const currentSheetAnalysis = useMemo(
    () => deepAnalysisBySheet[activeSheet] || null,
    [deepAnalysisBySheet, activeSheet],
  );

  const hasData = !!datasetId;

  return {
    // state
    datasetId,
    filename,
    sheetNames,
    sheetsInfo,
    activeSheet,
    currentSheetInfo,
    workbookOverview,
    loading,
    error,
    hasData,
    // analysis state
    deepAnalysisBySheet,
    currentSheetAnalysis,
    analysisLoading,
    analysisError,
    // actions
    loadFromUpload,
    loadFromGoogleSheet,
    loadFromHistory,
    switchSheet,
    reset,
    analyzeCurrentSheet,
  };
}
